import { Component, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { isChunkLoadError } from '@/shared/lib/lazy-with-retry';

/**
 * Safety net for lazy route mounts. `<Suspense>` handles the *pending*
 * import promise but NOT a rejection — a failed dynamic import (most
 * often a stale chunk after a deploy; see `lazy-with-retry.ts`) would
 * otherwise propagate uncaught and white-screen the app.
 *
 * `lazyWithRetry` auto-reloads once on the first stale-chunk failure;
 * this boundary catches the case where that reload didn't fix it (or any
 * other render error in the route subtree) and renders a graceful
 * reload panel in the content area instead of a blank page.
 *
 * Reset model: the consumer keys this boundary by `location.pathname`
 * (`<RouteErrorBoundary key={pathname}>`), so navigating to a new route
 * remounts it and clears the error state.
 */

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(err: unknown): State {
    return { hasError: true, isChunkError: isChunkLoadError(err) };
  }

  componentDidCatch(err: unknown) {
    // Load/render failure — not PHI, safe to log. Helps triage in dev
    // and surfaces in any console-forwarding telemetry.
    console.error('[RouteErrorBoundary]', err);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { isChunkError } = this.state;
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-[length:var(--type-body-strong-size)] leading-[var(--type-body-strong-line)] font-semibold text-[var(--ink-body)]">
          {isChunkError ? 'A new version is available' : 'Something went wrong'}
        </p>
        <p className="max-w-sm [font:var(--type-body)] text-[var(--ink-meta)]">
          {isChunkError
            ? 'This app was updated since you opened it. Reload to load the latest version.'
            : 'This page failed to load. Reloading usually fixes it.'}
        </p>
        <Button type="button" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    );
  }
}
